import { buildGrainBackground } from '@slite/components'

import { useIsMobile } from '../../utils/responsive'
import { CircuitBoardConnector } from './CircuitBoardConnector'

/* eslint-disable formatjs/no-literal-string-in-jsx */
const MCP_SERVER_SIZE = 300
const CARD_PADDING = 20
const PIN_GAP = 1

const GRAIN_BACKGROUND_DARK = buildGrainBackground({
  amplitude: 0.2,
  exponent: 6,
  baseFrequency: 2,
})

const GRADIENT_LIGHT = 'hsla(0, 0%, 87%, 1)'
const GRADIENT_DARK = 'hsla(0, 2%, 35%, 1)'
const TEXT_GRADIENT = `linear-gradient(to left, ${GRADIENT_DARK}, ${GRADIENT_LIGHT})`

// Same flower path as SliteLogo's symbol-no-bg variant, copied because that
// component only paints in currentColor and CSS can't gradient-fill an icon.
function GradientSliteSymbol({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 194 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient
          id="mcp-slite-logo-gradient"
          x1="0"
          y1="0"
          x2="1"
          y2="0"
        >
          <stop offset="0%" stopColor={GRADIENT_LIGHT} />
          <stop offset="100%" stopColor={GRADIENT_DARK} />
        </linearGradient>
      </defs>
      <path
        d="M194 78.7074C194 92.2284 184.011 103.45 171.048 105.393C164.864 95.8367 154.241 89.4132 142.111 89.1357C140.763 85.9239 140.01 82.395 140.01 78.7074C140.01 63.7986 152.14 51.7051 167.005 51.7051C181.87 51.7051 194 63.8383 194 78.7074ZM164.072 11.0231C149.167 11.0231 137.076 23.1563 137.076 38.0255C137.076 43.7749 138.9 49.1277 141.992 53.529C148.414 47.1452 157.253 43.2197 167.005 43.2197C174.933 43.2197 182.266 45.8367 188.173 50.238C190.036 46.5901 191.106 42.4267 191.106 38.0651C191.067 23.1563 178.976 11.0231 164.072 11.0231ZM106.791 132.95C104.809 132.474 102.748 132.236 100.647 132.236C85.7421 132.236 73.6518 144.37 73.6518 159.239C73.6518 174.108 85.7818 186.241 100.647 186.241C115.512 186.241 127.642 174.108 127.642 159.239C127.642 158.644 127.602 158.089 127.563 157.494C117.256 153.172 109.407 144.052 106.791 132.95ZM65.1291 159.239C65.1291 154.758 65.9616 150.476 67.5076 146.511C65.8427 146.194 64.1381 145.995 62.3543 145.995C47.4495 145.995 35.3592 158.128 35.3592 172.998C35.3592 187.906 47.4892 200 62.3543 200C70.7581 200 78.2501 196.154 83.2051 190.127C72.423 184.06 65.1291 172.482 65.1291 159.239ZM2.49734 160.111C2.49734 175.02 14.6273 187.113 29.4924 187.113C29.5717 187.113 29.6906 187.113 29.7699 187.113C27.9068 182.791 26.8365 178.033 26.8365 173.037C26.8365 158.525 35.5574 146.075 48.0441 140.563C43.208 135.964 36.6673 133.109 29.4924 133.109C14.6273 133.109 2.49734 145.242 2.49734 160.111ZM26.9951 90.5631C12.0903 90.5631 0 102.696 0 117.565C0 123.711 2.0613 129.381 5.54965 133.941C11.8525 128.152 20.2562 124.623 29.4924 124.623C37.2223 124.623 44.3972 127.121 50.2244 131.324C52.6424 127.28 54.0298 122.561 54.0298 117.565C54.0298 102.657 41.8999 90.5631 26.9951 90.5631ZM60.293 40.0476C46.1414 40.0476 34.6457 51.5465 34.6457 65.7019C34.6457 79.8573 46.1414 91.3561 60.293 91.3561C74.4446 91.3561 85.9403 79.8573 85.9403 65.7019C85.9403 51.5465 74.4446 40.0476 60.293 40.0476ZM141.358 97.8192C126.453 97.8192 114.402 109.913 114.402 124.782C114.402 139.651 126.492 151.745 141.358 151.745C156.223 151.745 168.313 139.651 168.313 124.782C168.313 109.913 156.262 97.8192 141.358 97.8192ZM99.973 26.9628C99.973 41.8716 112.103 53.9652 126.968 53.9652C128.712 53.9652 130.417 53.8066 132.082 53.4894C129.822 48.8105 128.554 43.5766 128.554 38.0651C128.554 25.1785 135.451 13.878 145.758 7.65275C140.882 2.93428 134.262 9.72247e-05 126.928 9.72247e-05C112.103 -0.0395538 99.973 12.054 99.973 26.9628Z"
        fill="url(#mcp-slite-logo-gradient)"
      />
    </svg>
  )
}

const pinDots = [
  0, 0, 0, 0, 0, 1, 0, 1, 1, 1, 1, 1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
]

// Trace endpoints of the circuit connector, aligned with filled pins of the
// strip above. Derived from the same paddings/gaps that render the pins so
// the two can't drift apart.
const pinCenter = (index: number) => {
  const innerHeight = MCP_SERVER_SIZE - 2 * CARD_PADDING
  const cellHeight =
    (innerHeight - (pinDots.length - 1) * PIN_GAP) / pinDots.length
  return CARD_PADDING + index * (cellHeight + PIN_GAP) + cellHeight / 2
}
const CONNECTOR_TARGETS = [7.6, 10, 12.4, 27.75, 30].map(pinCenter)

function ServerCard() {
  return (
    <div
      style={{
        height: MCP_SERVER_SIZE,
        width: '100%',
        maxWidth: MCP_SERVER_SIZE,
        flexShrink: 0,
      }}
    >
      <div
        className="width-full height-full flexbox background-moon-dust"
        style={{ borderRadius: 24, padding: CARD_PADDING }}
      >
        <div
          className="height-full flexbox flexbox-column"
          style={{ width: 4, gap: PIN_GAP }}
        >
          {pinDots.map((dot, index) => (
            <div
              key={index}
              style={{
                width: '100%',
                flex: 1,
                background: dot ? 'var(--saturn-orange)' : 'transparent',
              }}
            />
          ))}
        </div>
        <div
          className="height-full flexbox overflow-hidden background-shade-40"
          style={{
            flex: 1,
            border: '1px solid var(--shade-00)',
            borderRadius: 14,
            paddingLeft: 1,
          }}
        >
          <div
            className="height-full flexbox flexbox-column flexbox-space-between flexbox-gap-8 padding-24 radius-larger"
            style={{
              backgroundColor: '#2A2A2A',
              backgroundImage: GRAIN_BACKGROUND_DARK,
              backgroundBlendMode: 'screen',
              flex: 1,
            }}
          >
            <GradientSliteSymbol size={24} />
            <p
              className="font-weight-semibold font-size-4 width-fit-content"
              style={{
                backgroundImage: TEXT_GRADIENT,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Slite MCP Server
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export const McpVisual = () => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div
        className="flexbox flexbox-column flexbox-align-center padding-20 margin-left-auto margin-right-auto"
        style={{ maxWidth: 800 }}
      >
        <div
          className="flexbox flexbox-column width-full"
          style={{ maxWidth: MCP_SERVER_SIZE }}
        >
          <ServerCard />
          <div className="width-full" style={{ height: 260, marginTop: -2 }}>
            <CircuitBoardConnector
              orientation="vertical"
              targets={CONNECTOR_TARGETS}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="flexbox padding-40 margin-left-auto margin-right-auto"
      style={{ maxWidth: 800 }}
    >
      <div
        className="min-width-zero"
        style={{
          flex: 1,
          height: MCP_SERVER_SIZE,
          marginRight: -2,
        }}
      >
        <CircuitBoardConnector
          orientation="horizontal"
          targets={CONNECTOR_TARGETS}
        />
      </div>
      <ServerCard />
    </div>
  )
}
